import { useState } from 'react';

import { SMTFoodEntryData } from '@equip.health/ui';

import AnalyticsEvents from '~/analytics';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import {
  useCreateSMTMealMutation,
  useUploadSMTEntryImageMutation,
} from '~/store/services/SMT';

import { transformFoodEntryDataToCreateRequest } from '~/components/maud/patients/profile/smt/foodEntryUtils';
import { uploadSMTImage } from '~/components/maud/patients/profile/smt/hooks/uploadSMTImage.util';

interface UseAddSMTEntryParams {
  patientId: string;
}

interface UseAddSMTEntryReturn {
  handleAddEntry: () => void;
  handleCloseAddModal: () => void;
  handleConfirmAdd: (data: SMTFoodEntryData) => Promise<void>;
  isAddModalOpen: boolean;
  isLoading: boolean;
}

export const useAddSMTEntry = ({
  patientId,
}: UseAddSMTEntryParams): UseAddSMTEntryReturn => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent } = useAnalytics();
  const { logError } = useDatadog();
  const [createSMTMeal] = useCreateSMTMealMutation();
  const [uploadSMTEntryImage] = useUploadSMTEntryImageMutation();

  const handleAddEntry = () => {
    setIsAddModalOpen(true);
    trackEvent(AnalyticsEvents.addEntryButtonClicked, {
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });
  };

  const handleConfirmAdd = async (data: SMTFoodEntryData) => {
    trackEvent(AnalyticsEvents.addEntryConfirmed, {
      patientId,
      source: AnalyticsSourceValues.SMT_TAB,
    });

    setIsLoading(true);
    try {
      const createResponse = await createSMTMeal(
        transformFoodEntryDataToCreateRequest(data, patientId),
      ).unwrap();

      const entryId = createResponse?.smtEntryId;
      if (entryId) {
        try {
          await uploadSMTImage({
            data,
            entryId,
            errorContext: {
              action: 'addSMTEntry',
              context: 'SMT image compression failed',
              entryId,
              patientId,
            },
            logError,
            patientId,
            source: AnalyticsSourceValues.SMT_TAB,
            trackEvent,
            uploadSMTEntryImage,
          });
        } catch (imageError) {
          logError(imageError, {
            action: 'addSMTEntry',
            context: 'SMT entry image upload failed after entry creation',
            entryId,
            patientId,
          });
        }
      } else {
        logError(new Error('Entry ID not found in create response'), {
          action: 'addSMTEntry',
          context:
            'SMT entry created but entry ID missing, skipping image upload',
          patientId,
        });
      }

      setIsAddModalOpen(false);
    } catch (error) {
      logError(error, {
        action: 'addSMTEntry',
        context: 'Failed to create SMT meal entry',
        patientId,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAddModal = () => {
    if (isAddModalOpen) {
      trackEvent(AnalyticsEvents.addEntryCanceled, {
        patientId,
        source: AnalyticsSourceValues.SMT_TAB,
      });
    }
    setIsAddModalOpen(false);
  };

  return {
    handleAddEntry,
    handleCloseAddModal,
    handleConfirmAdd,
    isAddModalOpen,
    isLoading,
  };
};
